import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './FirmasModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Firmas";
const endpoint = ENDPOINTS.firmas;
const endpointById = ENDPOINTS.firmasById;
const modelo = Models.firmas;

const maps = {
  mapCreate: (f) => ({
    idEmpleado: Number(f.idEmpleado) || 0,
    firmaUrl: f.firmaUrl,
    fechaRegistro: f.fechaRegistro || null,
    fechaVigencia: f.fechaVigencia || null,
    esDirector: !!f.esDirector,
    activo: true,
  }),
  mapUpdate: (id, f) => ({
    idFirma: id,
    idEmpleado: Number(f.idEmpleado) || 0,
    firmaUrl: f.firmaUrl,
    fechaRegistro: f.fechaRegistro || null,
    fechaVigencia: f.fechaVigencia || null,
    esDirector: !!f.esDirector,
    activo: true,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resFirmas, resEmp, resPer] = await Promise.all([
        axios.get(endpoint),
        axios.get(ENDPOINTS.empleados),
        axios.get(ENDPOINTS.personas)
      ]);
      setRows((resFirmas.data || []).map((x) => modelo(x)));
      setEmpleados((resEmp.data || []).map(x => Models.empleado(x)));
      setPersonas((resPer.data || []).map(x => Models.personas(x)));
    } catch (e) {
      console.error(e);
      triggerAlert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const getEmpleadoNombre = (idEmpleado) => {
    const emp = empleados.find(e => e.idEmpleado === idEmpleado);
    if (!emp) return `Empleado ${idEmpleado}`;
    const per = personas.find(p => p.idPersona === emp.idPersona);
    return per ? `${per.nombre} ${per.apellidoPaterno} ${per.apellidoMaterno || ''}`.trim() : `Empleado ${idEmpleado}`;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const empNombre = getEmpleadoNombre(r.idEmpleado).toLowerCase();
      const url = (r.firmaUrl || '').toLowerCase();
      const dir = r.esDirector ? 'si' : 'no';
      const act = r.activo ? 'si' : 'no';
      const fReg = (r.fechaRegistro || '').toLowerCase();
      const fVig = (r.fechaVigencia || '').toLowerCase();
      return empNombre.includes(q) || url.includes(q) || dir.includes(q) || act.includes(q) || fReg.includes(q) || fVig.includes(q);
    });
  }, [rows, query, empleados, personas]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idFirma === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);

      // Si se marca como director, debemos quitar el rol de director a todos los demás
      if (payload.esDirector) {
        // Encontrar la firma que actualmente es director (si existe)
        // Ignorar el registro actual si estamos editando
        const currentDirector = rows.find(r => r.esDirector && (mode === 'new' || r.idFirma !== selectedId));

        if (currentDirector) {
          // Actualizar el director actual a false
          const updatePayload = {
            ...currentDirector,
            esDirector: false
          };
          // Usamos mapUpdate recordando que necesita el ID y el objeto 'f' (que es updatePayload)
          // IMPORTANTE: mapUpdate espera un objeto con las propiedades SIN mapear o CON mapear? 
          // Revisando maps.mapUpdate: toma (id, f) donde f son los campos del formulario.
          // currentDirector ya tiene los campos mapeados (es una fila procesada por modelo).
          // Necesitamos pasarle un objeto compatible con lo que espera mapUpdate en 'f'.
          // Reconstruimos 'f' a partir de currentDirector
          const fForUpdate = {
            idEmpleado: currentDirector.idEmpleado,
            firmaUrl: currentDirector.firmaUrl,
            fechaRegistro: currentDirector.fechaRegistro,
            fechaVigencia: currentDirector.fechaVigencia,
            esDirector: false, // Forzar falso
            activo: currentDirector.activo
          };

          const updateMap = maps.mapUpdate(currentDirector.idFirma, fForUpdate);
          await axios.put(endpoint, updateMap);
        }
      }

      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idFirma ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por id empleado, URL, fechas, director o activo…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>Fecha Registro</th>
                  <th>Fecha Vigencia</th>
                  <th>Es Director</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idFirma}>
                    <td>{getEmpleadoNombre(r.idEmpleado)}</td>
                    <td>{normalizeValue(r.fechaRegistro)}</td>
                    <td>{normalizeValue(r.fechaVigencia)}</td>
                    <td>{r.esDirector ? 'Sí' : 'No'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--edit"
                        onClick={() => handleEdit(r.idFirma)}
                        aria-label="Editar"
                        title="Editar"
                      >
                        <img src={sigmaEdit} alt="" className="rk-ico" />
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--del"
                        onClick={() => {
                          setEditingRowId(null);
                          const name = `Empleado ${r.idEmpleado}`;
                          askDelete(r.idFirma, name);
                        }}
                        aria-label="Borrar"
                        title="Borrar"
                      >
                        <img src={sigmaDelete} alt="" className="rk-ico" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && paged.length === 0 && (<tr><td colSpan={8}>Sin resultados.</td></tr>)}
              </tbody>
            </table >
          </div >
        </div >

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {
          modalOpen && (
            <div className="roles-editor-slot" ref={editorRef}>
              <Modal
                open={modalOpen}
                mode={modalMode}
                data={editData}
                empleados={empleados}
                personas={personas}
                inline
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
              />
            </div>
          )
        }
      </div >

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div >
  );
}
